import { createFileRoute } from "@tanstack/react-router";
import { Layout } from "@/components/Layout";
import { GoldRateCalculator } from "@/components/GoldRateCalculator";
import { FaqSection } from "@/components/FaqSection";
import { goldRatesQueryOptions } from "@/lib/nonblocking-rates";
import { SITE_URL, DEFAULT_OG_IMAGE, hreflangLinks } from "@/lib/seo";

const PATH = "/today-gold-rate-calculator-pakistan";
const TITLE = "Today Gold Rate Calculator Pakistan — Per Tola, Gram, 22K, 24K Live";
const DESC = "Free gold price calculator Pakistan: aaj ka rate per tola, per gram, 10 gram, masha aur ratti — 24K, 22K, 21K, 18K, 14K sabhi karat. Karachi, Lahore, Islamabad live sarafa bazaar rate.";

const FAQS = [
  {
    q: "Aaj Pakistan mein 1 tola sone ka rate calculator kaise use karein?",
    a: "Upar diye gaye Today Gold Rate Calculator Pakistan mein karat (24K/22K/21K/18K/14K) select karein, unit mein 'Tola' chunein, quantity 1 rakhein aur apna city (Karachi, Lahore, Islamabad, Rawalpindi, Peshawar, Quetta, Multan ya Faisalabad) select karein. Live sarafa bazaar rate ke hisaab se instant total dikhega — koi manual calculation nahi.",
  },
  {
    q: "Gold price calculator 22K Pakistan ka formula kya hai?",
    a: "22K gold price = (24K per tola rate × 22 ÷ 24) × tola quantity. Hamara calculator yeh formula automatic apply karta hai. Example: agar 24K per tola Rs 400,000 hai to 22K per tola ≈ Rs 366,666 hoga, aur 22K per gram ≈ Rs 31,438 (yaani 366,666 ÷ 11.6638).",
  },
  {
    q: "Today gold rate in Pakistan 22K per tola calculator kis rate se calculate karta hai?",
    a: "Yeh calculator London bullion market ka live spot rate (USD/oz) leta hai, use PKR/USD interbank rate se convert karta hai, phir Pakistan ka local sarafa premium (approx Rs 4,000/tola) aur city premium add karke aapko city-specific 22K per tola rate deta hai. Data har 10 minute mein refresh hota hai.",
  },
  {
    q: "Gold weight calculator online — masha, ratti, tola ka relation kya hai?",
    a: "1 Tola = 12 Masha, 1 Masha = 8 Ratti, 1 Tola = 96 Ratti = 11.6638 grams. Hamara calculator masha aur ratti dono support karta hai — chhoti quantity (jaise chain ka pendant ya nose pin) ke liye ratti/masha unit chunein aur exact price nikaalein.",
  },
  {
    q: "Karachi aur Lahore mein per tola rate mein farq kyun hota hai?",
    a: "Karachi (Sarafa Bazar Boulton Market) Pakistan ka base benchmark hai. Lahore, Islamabad, Rawalpindi mein transport, sarafa association margin aur demand ke wajah se ~0.08%–0.15% premium lagta hai. Calculator har city ka premium automatically apply karta hai, is liye Lahore ka rate Karachi se Rs 300–600 per tola upar dikhega.",
  },
  {
    q: "24K aur 22K gold Pakistan mein — jewellery ke liye kaunsa best hai?",
    a: "24K (99.9% pure) invest aur savings ke liye best hai lekin bahut soft hota hai, jewellery mein nahi banta. 22K (91.67% pure) Pakistan ki standard jewellery karat hai — bangles, chains, rings, sets sab 22K mein banti hain kyunki isme thoda copper/silver mila hota hai jo strength deta hai. Wedding jewellery ke liye 22K choose karein.",
  },
  {
    q: "Gold price calculator Pakistan making charges bhi calculate karta hai?",
    a: "Nahi — yeh calculator sirf pure gold ka rate (metal value) calculate karta hai. Jewellery kharidne par sarafa Rs 800–2,500 per gram making charges aur 3% GST alag charge karta hai. Ready jewellery ka total = (calculator rate) + (making charges × grams) + tax. Investment gold biscuits/bars par making charges bahut kam hote hain.",
  },
  {
    q: "10 gram gold ka rate Pakistan mein kaise nikaalein is calculator se?",
    a: "Unit dropdown mein '10 Gram' chunein aur karat select karein — calculator turant per 10g rate dikha dega. Alternatively, per gram rate ko 10 se multiply karein: 24K per gram Rs 34,300 hai to 24K per 10 gram Rs 343,000 hoga. India-style buyers (jo 10 gram mein sochte hain) ke liye yeh useful hai.",
  },
  {
    q: "Kya calculator per ounce aur per kilogram bhi calculate karta hai?",
    a: "Haan. Unit mein 'Ounce (31.1035g)' ya 'Kilogram (1000g)' chunein. 1 ounce ≈ 2.6667 tola, aur 1 kg = 85.735 tola. Bullion investors, exporters aur wholesale sarafa yeh unit use karte hain. Calculator sabhi karat par ounce/kg rate live nikaalta hai.",
  },
  {
    q: "Gold rate calculator Pakistan ke saath price alert kaise set karein?",
    a: "Har city page (jaise /pakistan ya /city/pakistan/karachi) par 'Price Alert' bell icon hai jahan aap target rate set kar sakte hain. Jab bhi live rate aapke target ko touch kare, DrHint push notification bhejta hai — koi manual refresh nahi. Yeh calculator + alert combination Pakistan ke sarafa traders aur bachat karne walon dono ke liye best hai.",
  },
];

export const Route = createFileRoute("/today-gold-rate-calculator-pakistan")({
  loader: ({ context }) => context.queryClient.prefetchQuery(goldRatesQueryOptions),
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { name: "robots", content: "index,follow,max-image-preview:large,max-snippet:-1" },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: `${SITE_URL}${PATH}` },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "en_PK" },
      { property: "og:image", content: DEFAULT_OG_IMAGE },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESC },
      { name: "twitter:image", content: DEFAULT_OG_IMAGE },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}${PATH}` }, ...hreflangLinks(PATH)],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: PakistanCalcPage,
});

function PakistanCalcPage() {
  return (
    <Layout>
      <div className="mx-auto max-w-6xl px-4 pt-6">
        <h1 className="text-3xl font-black md:text-4xl">
          Today Gold Rate Calculator Pakistan — Live Per Tola, Gram & 22K/24K
        </h1>
        <p className="mt-2 text-muted-foreground">
          Free live <strong>gold calculator Pakistan per tola today</strong> — 24K, 22K, 21K, 18K, 17K aur 14K sabhi karat, sabhi cities. Chart, aaj ki date aur city-wise instant calculation ek page par.
        </p>
      </div>

      <GoldRateCalculator country="pakistan" />

      {/* Long-form 400+ words unique copy */}
      <section className="mx-auto max-w-4xl px-4 py-10">
        <h2 className="text-2xl font-bold">
          Gold Rate Calculator Pakistan — Aaj Ka Sona Kaise Calculate Karein
        </h2>
        <div className="mt-4 space-y-4 text-sm leading-relaxed text-muted-foreground">
          <p>
            Pakistan mein sarafa bazaar (Boulton Market Karachi, Sarafa Bazaar Lahore, Blue Area Islamabad) har roz subah 10 baje aur dopahar 3 baje rate revise karta hai. Agar aap 1 tola, 5 tola, 10 tola ya 100 gram gold kharidne, bechne ya zakat calculate karne ja rahe hain, to <strong>Today Gold Rate Calculator Pakistan</strong> aapko manual calculation ki jhanjhat se bacha deta hai — bas karat aur city select karein, total automatically nikal aata hai.
          </p>
          <p>
            Is calculator ka core formula international spot price (USD per troy ounce) ko PKR/USD interbank rate se convert karke local sarafa premium add karta hai. Formula: <em>(spot USD/oz ÷ 2.6667) × PKR/USD + Rs 4,000 premium × city multiplier</em>. Yehi wajah hai ki hamara <strong>gold price calculator Pakistan</strong> ka rate PSGJA (Pakistan Sarafa Gems &amp; Jewellers Association) ke declared rate ke +/- 1% ke andar rehta hai.
          </p>
          <p>
            <strong>Gold weight calculator online</strong> feature bhi included hai — Pakistan mein rozmarra ki quantities tola (11.6638g), masha (0.972g) aur ratti (0.121g) mein hoti hain. Modern jewellery invoices gram mein hoti hain lekin dada-dadi ke gehne aksar tola/masha mein documented hote hain. Aap koi bhi unit chunein, calculator conversion behind-the-scenes handle karta hai. Ek chain jo 3 tola 4 masha ki hai, uska exact 22K value seconds mein pata chalega.
          </p>
          <p>
            <strong>Gold price calculator 22K Pakistan</strong> ke liye karat dropdown se "22K" chunein — bridal jewellery, bangles (choorian), gulubund, rings sab 22K mein banti hain. 22K rate = 24K rate × 0.9167. Yeh isliye important hai kyunki sarafa aksar 24K rate quote karta hai lekin actual jewellery 22K ya 21K mein deta hai — customer ko sahi price pata hona chahiye. Hamara calculator har karat ka rate side-by-side dikhata hai taake compare aasan ho.
          </p>
          <p>
            City-wise variations bhi handle hoti hain. <strong>Today gold rate in Pakistan 22K per tola</strong> Karachi aur Lahore mein farq isliye hota hai kyunki Karachi import hub hai (base rate), Lahore mein transport + sarafa margin add hota hai, Islamabad/Rawalpindi mein embassy demand ka premium hai, aur Peshawar/Quetta mein cross-border smuggled gold ki wajah se kabhi rate slightly kam hota hai. Calculator har city ka realistic premium apply karta hai.
          </p>
          <p>
            Deep-linking bhi supported hai: URL mein <code>#22k-per-tola-lahore</code> add karein, calculator directly wohi combination load kar dega — WhatsApp par share karne, blog embed karne ya bookmark karne ke liye perfect. Zakat calculation ke liye chart ke neeche zakat nisab section dekhein, aur agar aap regularly gold track karte hain to Price Alert bell zaroor set karein.
          </p>
        </div>
      </section>

      <FaqSection faqs={FAQS} title="Log Ye Bhi Poochte Hain — Pakistan Gold Calculator FAQs" />

      <section className="mx-auto max-w-4xl px-4 pb-12">
        <h2 className="text-xl font-bold">Related Live Rates</h2>
        <ul className="mt-3 grid gap-2 sm:grid-cols-2 text-sm">
          <li><a className="text-primary underline" href="/pakistan">Live Gold Rate Pakistan (All Cities)</a></li>
          <li><a className="text-primary underline" href="/pakistan/per-tola">Gold Per Tola Pakistan</a></li>
          <li><a className="text-primary underline" href="/pakistan/per-gram">Gold Per Gram Pakistan</a></li>
          <li><a className="text-primary underline" href="/pakistan/per-10-gram">Gold Per 10 Gram Pakistan</a></li>
          <li><a className="text-primary underline" href="/pakistan/21k">21K Gold Pakistan</a></li>
          <li><a className="text-primary underline" href="/pakistan/18k">18K Gold Pakistan</a></li>
          <li><a className="text-primary underline" href="/pakistan/zakat-nisab">Zakat Nisab Calculator</a></li>
          <li><a className="text-primary underline" href="/today-gold-rate-calculator-india">India Gold Calculator</a></li>
        </ul>
      </section>
    </Layout>
  );
}